// Which routine runs on which day of the week.
//
// Seven slots, said once. After that the nightly pass keeps the next four weeks
// planned, so a week arrives already knowing what it needs instead of waiting
// for somebody to remember to plan it — and a coverage warning nobody has
// planned against is a warning that never fires.
//
// An empty slot means that weekday isn't planned, which is how a barn says "we
// don't open Sundays" without needing a second setting for it.

import { useEffect, useState } from 'react';
import { fmtDate } from '../../lib/dates';

const DAYS = [
  [1, 'Mon'],
  [2, 'Tue'],
  [3, 'Wed'],
  [4, 'Thu'],
  [5, 'Fri'],
  [6, 'Sat'],
  [0, 'Sun'],
];

function longDate(iso) {
  return fmtDate(iso, 'dayMedium');
}

export default function WeekdayMap({ routines = [], value, plannedThrough, saving, onSave }) {
  // { weekday: routineId }. Held locally so seven selects can be changed and
  // saved as one thing rather than firing seven writes.
  const [map, setMap] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const next = {};
    for (const w of value ?? []) next[w.weekday] = w.routineId;
    setMap(next);
    setDirty(false);
  }, [value]);

  const set = (day, routineId) => {
    setMap((prev) => {
      const next = { ...prev };
      if (routineId) next[day] = routineId;
      else delete next[day];
      return next;
    });
    setDirty(true);
  };

  const filled = Object.keys(map).length;

  return (
    <section className="card p-4">
      <header className="mb-3">
        <h2 className="font-medium text-ink-900">The barn&apos;s week</h2>
        <p className="text-sm text-ink-500">
          Say which routine runs on each day and the next four weeks stay planned on their own. Leave
          a day blank and it simply isn&apos;t planned.
        </p>
      </header>

      {routines.length === 0 ? (
        <p className="text-sm text-ink-400">Add a routine above first — there is nothing to map yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {DAYS.map(([day, label]) => (
              <div key={day}>
                <label
                  className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1"
                  htmlFor={`wd-${day}`}
                >
                  {label}
                </label>
                <select
                  id={`wd-${day}`}
                  className="input w-full"
                  value={map[day] ?? ''}
                  onChange={(e) => set(day, e.target.value)}
                >
                  <option value="">—</option>
                  {routines.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !dirty}
              onClick={() => onSave(map)}
            >
              {saving ? 'Saving…' : 'Save the week'}
            </button>

            <p className="text-sm text-ink-500">
              {filled === 0 ? (
                // The honest description of an empty map: it is the off switch,
                // and saying so beats leaving somebody to infer it.
                'No days mapped, so nothing is planned automatically.'
              ) : plannedThrough ? (
                <>Planned through <strong>{longDate(plannedThrough)}</strong>.</>
              ) : (
                'Save to plan the next four weeks.'
              )}
            </p>
          </div>

          {plannedThrough && (
            <p className="mt-2 text-xs text-ink-400">
              Days already planned are left alone — including ones you have emptied on purpose. Only
              days beyond {longDate(plannedThrough)} get filled in.
            </p>
          )}
        </>
      )}
    </section>
  );
}
